import type { ElementsTypes, EditorElement } from '@vedalib/editor/lib/elements'
import { generateId } from '@vedalib/editor/lib/utils/id'
import lodash from 'lodash'

import type { FCElementType } from './elements.types'
import Accordion from './elements/Accordion'
import Button from './elements/Button'
import Card from './elements/Card'
import Chart from './elements/Chart'
import Checkbox from './elements/Checkbox'
import Code from './elements/Code'
import CourseProgress from './elements/CourseProgress'
import CourseStructure from './elements/CourseStructure'
import Divider from './elements/Divider'
import Downloader from './elements/Downloader'
import Embedded from './elements/Embedded'
import Grouping from './elements/Grouping'
import Image from './elements/Image'
import LabeledGraphic from './elements/LabeledGraphic'
import LayoutList from './elements/LayoutList'
import Link from './elements/Link'
import List from './elements/List'
import Audio from './elements/Media/AudioElement'
import Video from './elements/Media/VideoElement'
import Rectangle from './elements/Rectangle'
import Reflection from './elements/Reflection'
import RichText from './elements/RichText'
import Simulator from './elements/Simulator'
import Slider from './elements/Slider'
import Splitter from './elements/Splitter'
import Table from './elements/Table'
import Tabs from './elements/Tabs'
import TestFeedback from './elements/TestFeedback'
import TestGeneralFeedback from './elements/TestGeneralFeedback'
import TestGrouping from './elements/TestGrouping'
import Hotspot from './elements/TestHotspot'
import { StartTestInfo, EndTestInfo } from './elements/TestInfo'
import KeyboardInput from './elements/TestKeyboardInput'
import TestMatching from './elements/TestMatching'
import TestMultipleChoice from './elements/TestMultipleChoice'
import TestRanking from './elements/TestRanking'
import TestResult from './elements/TestResult'
import TestSingleChoice from './elements/TestSingleChoice'

export const ELEMENTS_CONFIG = {
  [RichText.type]: RichText,
  [Image.type]: Image,
  [Audio.type]: Audio,
  [Video.type]: Video,
  [List.type]: List,
  [Button.type]: Button,
  [Divider.type]: Divider,
  [Table.type]: Table,
  [Rectangle.type]: Rectangle,
  [LabeledGraphic.type]: LabeledGraphic,
  [Card.type]: Card,

  // SIMPLE FIGURES
  [Code.type]: Code,
  [Embedded.type]: Embedded,

  // COVER
  [CourseStructure.type]: CourseStructure,
  [CourseProgress.type]: CourseProgress,
  [LayoutList.type]: LayoutList,

  [Reflection.type]: Reflection,

  // QUESTIONS
  [TestSingleChoice.type]: TestSingleChoice,
  [TestMultipleChoice.type]: TestMultipleChoice,
  [TestRanking.type]: TestRanking,
  [TestMatching.type]: TestMatching,
  [KeyboardInput.type]: KeyboardInput,
  [Hotspot.type]: Hotspot,
  [TestGrouping.type]: TestGrouping,

  // TEST
  [TestFeedback.type]: TestFeedback,
  [TestResult.type]: TestResult,
  [StartTestInfo.type]: StartTestInfo,
  [EndTestInfo.type]: EndTestInfo,
  [TestGeneralFeedback.type]: TestGeneralFeedback,

  // RECURSIVE S
  [Tabs.type]: Tabs,
  [Accordion.type]: Accordion,
  [Slider.type]: Slider,

  [Grouping.type]: Grouping,
  [Splitter.type]: Splitter,
  [Chart.type]: Chart,
  [Downloader.type]: Downloader,
  [Link.type]: Link,
  [Simulator.type]: Simulator,
  [Checkbox.type]: Checkbox,
}

export const ElementNotFound = (type: string) => () => <div>Element not Found: {type}</div>

export const getEditorElementComponent = (type: ElementsTypes): FCElementType => {
  const res = ELEMENTS_CONFIG[type]
  if (res && res.Element) {
    return res.Element as unknown as FCElementType
  }

  return ElementNotFound(type)
}

export const getElementDefaultValue = (type: ElementsTypes) => ELEMENTS_CONFIG[type].defaultValue

export const generateElement = (type: ElementsTypes) => {
  const { defaultValue } = ELEMENTS_CONFIG[type]

  return lodash.cloneDeep({
    id: generateId(),
    type: type,
    style: {},
    font: {},
    value: defaultValue,

    containerOptions: {}, // deprecated (need on backend)
    options: {}, // deprecated (need on backend)
  }) as unknown as EditorElement
}

export const NO_PANEL_ELEMENTS: Array<keyof typeof ELEMENTS_CONFIG> = [
  ELEMENTS_CONFIG.richText.type,
  ELEMENTS_CONFIG.table.type,
]
